import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import s from './context-panel-header.scss';

export interface ContextPanelHeaderProps {
  /**
   * The header content — title text, navigation arrows, badges, etc.
   * Compose freely; the close button is managed separately via `onClose`.
   */
  children: ReactNode;
  className?: string;
  /**
   * Called when the close button is clicked. When omitted, no close button
   * is rendered and no tinted background is applied (persistent panel style).
   */
  onClose?: () => void;
}

/**
 * A convenience header component for consistent panel header styling. Use as
 * the `header` value when calling `open()` on a context panel, or provide any
 * other ReactNode for full control.
 *
 * When `onClose` is provided, the header gets a tinted background and a close
 * button. When omitted, the header has no background — suitable for persistent
 * panels. The background colour is exposed as `--context-panel-header-background-color` for
 * per-implementation customisation.
 *
 * @example
 * // Simple persistent header (no close button, no tinted background):
 * <ContextPanel.Header>Recent Activity</ContextPanel.Header>
 *
 * @example
 * // Closable header with tinted background and navigation arrows:
 * <ContextPanel.Header onClose={close}>
 *   <button onClick={prev}><ChevronLeft /></button>
 *   <span>CVE-2021-39144</span>
 *   <button onClick={next}><ChevronRight /></button>
 * </ContextPanel.Header>
 */
export const ContextPanelHeader = ({
  children,
  className,
  onClose,
}: ContextPanelHeaderProps) => {
  useStyles(s);

  return (
    <header className={classNames(s.root, onClose && s.closable, className)}>
      <div className={s.content}>{children}</div>

      {onClose && (
        <button
          className={s.closeButton}
          aria-label="Close panel"
          onClick={onClose}
          type="button"
        >
          <X strokeWidth={1} />
        </button>
      )}
    </header>
  );
};

export default ContextPanelHeader;
